// First-party analytics. Records nothing until the visitor accepts the RGPD banner,
// never stores IPs, and ignores admin/staff/auth pages.
const URL_ = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const KEY_ = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
const IGNORED = ["/admin", "/pdv", "/colaborador", "/auth", "/admin-setup", "/reset-password"];

interface Ev { session_id: string; visitor_id: string | null; event: string; page: string; target?: string; product_id?: string; value?: number; props?: Record<string, unknown>; referrer?: string; utm_source?: string; utm_medium?: string; utm_campaign?: string; device: string; lang: string }

const queue: Ev[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let currentPath = "";

const safe = <T,>(fn: () => T, fallback: T): T => { try { return fn(); } catch { return fallback; } };
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const consented = () => safe(() => localStorage.getItem("rgpd_consent") === "accepted", false);
export const isTrackedPath = (p: string) => !IGNORED.some((i) => p === i || p.startsWith(i + "/"));

function ids() {
  const session = safe(() => { let s = sessionStorage.getItem("ms_sid"); if (!s) { s = uuid(); sessionStorage.setItem("ms_sid", s); } return s; }, uuid());
  const visitor = safe(() => { let v = localStorage.getItem("ms_vid"); if (!v) { v = uuid(); localStorage.setItem("ms_vid", v); } return v; }, null as string | null);
  return { session, visitor };
}

function attribution() {
  return safe(() => {
    const saved = sessionStorage.getItem("ms_attr");
    if (saved) return JSON.parse(saved);
    const q = new URLSearchParams(location.search);
    let ref = "";
    try { ref = document.referrer ? new URL(document.referrer).hostname.replace(/^www\./, "") : ""; } catch { /* ignore */ }
    if (ref === location.hostname) ref = "";
    const a = { referrer: ref || undefined, utm_source: q.get("utm_source") || undefined, utm_medium: q.get("utm_medium") || undefined, utm_campaign: q.get("utm_campaign") || undefined };
    sessionStorage.setItem("ms_attr", JSON.stringify(a));
    return a;
  }, {} as Record<string, string | undefined>);
}

const device = () => (window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop");

export function flush() {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length || !URL_ || !KEY_) return;
  const COLS = ["session_id", "visitor_id", "event", "page", "target", "product_id", "value", "props", "referrer", "utm_source", "utm_medium", "utm_campaign", "device", "lang"] as const;
  // PostgREST rejects bulk inserts whose objects have different keys (HTTP 400): normalise to the full column set.
  const batch = queue.splice(0, 50).map((e) => Object.fromEntries(COLS.map((c) => [c, (e as unknown as Record<string, unknown>)[c] ?? null])));
  try {
    fetch(`${URL_}/rest/v1/analytics_events`, {
      method: "POST", keepalive: true,
      headers: { "Content-Type": "application/json", apikey: KEY_, Authorization: `Bearer ${KEY_}`, Prefer: "return=minimal" },
      body: JSON.stringify(batch),
    }).catch(() => {});
  } catch { /* never break the site */ }
  if (queue.length) timer = setTimeout(flush, 1000);
}

export function track(event: string, extra: { target?: string; product_id?: string; value?: number; props?: Record<string, unknown>; page?: string } = {}) {
  if (!consented()) return;
  const page = extra.page ?? (currentPath || location.pathname);
  if (!isTrackedPath(page)) return;
  const { session, visitor } = ids();
  queue.push({
    session_id: session, visitor_id: visitor, event, page,
    target: extra.target?.slice(0, 120), product_id: extra.product_id, value: extra.value, props: extra.props,
    ...attribution(), device: device(), lang: (navigator.language || "").slice(0, 12),
  });
  if (queue.length >= 10) flush(); else if (!timer) timer = setTimeout(flush, 4000);
}

// ---- automatic behaviour tracking (page time, scroll depth, sections, clicks) ----
let pageStart = 0, accumulated = 0, sentMs = 0;
let scrollMarks = new Set<number>();
let sectionTimes = new Map<string, number>();
const sectionSince = new Map<string, number>();
const sectionActive = new Set<string>();
let sectionSeen = new Set<string>();
let io: IntersectionObserver | null = null;

/** Sends only the time not yet reported, so hide + route change never double count. */
function emitTimes(closing: boolean) {
  const now = Date.now();
  const total = accumulated + (pageStart ? now - pageStart : 0);
  const delta = total - sentMs;
  if (currentPath && delta > 500) { track("page_time", { value: Math.min(Math.round(delta / 1000), 3600) }); sentMs += delta; }
  sectionSince.forEach((since, id) => { sectionTimes.set(id, (sectionTimes.get(id) || 0) + (now - since)); sectionSince.set(id, now); });
  sectionTimes.forEach((ms, id) => { if (ms > 1000) track("section_time", { target: id, value: Math.min(Math.round(ms / 1000), 3600) }); });
  sectionTimes = new Map();
  if (closing) { sectionSince.clear(); sectionActive.clear(); sectionSeen = new Set(); accumulated = 0; sentMs = 0; pageStart = 0; io?.disconnect(); }
}

function observeSections() {
  io?.disconnect();
  if (!("IntersectionObserver" in window)) return;
  io = new IntersectionObserver((entries) => {
    const now = Date.now();
    entries.forEach((en) => {
      const id = (en.target as HTMLElement).id;
      if (en.isIntersecting) {
        sectionActive.add(id);
        if (document.visibilityState === "visible") sectionSince.set(id, now);
        if (!sectionSeen.has(id)) { sectionSeen.add(id); track("section_view", { target: id }); }
      } else {
        sectionActive.delete(id);
        if (sectionSince.has(id)) {
          sectionTimes.set(id, (sectionTimes.get(id) || 0) + (now - sectionSince.get(id)!));
          sectionSince.delete(id);
        }
      }
    });
  }, { threshold: 0.5 });
  document.querySelectorAll("section[id]").forEach((el) => io!.observe(el));
}

export function trackPageView(path: string) {
  emitTimes(true);
  currentPath = path;
  scrollMarks = new Set();
  pageStart = Date.now();
  if (!isTrackedPath(path)) return;
  track("page_view");
  // sections render lazily: scan a couple of times
  setTimeout(observeSections, 800);
  setTimeout(observeSections, 2500);
}

function onScroll() {
  const doc = document.documentElement;
  const max = doc.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const pct = ((window.scrollY || doc.scrollTop) / max) * 100;
  [25, 50, 75, 100].forEach((m) => { if (pct >= m - 2 && !scrollMarks.has(m)) { scrollMarks.add(m); track("scroll", { target: String(m) }); } });
}

function labelOf(el: Element): string {
  const h = el as HTMLElement;
  const explicit = h.closest("[data-track]")?.getAttribute("data-track");
  if (explicit) return explicit;
  const a = h.closest("a");
  const text = (h.getAttribute("aria-label") || h.textContent || "").trim().replace(/\s+/g, " ").slice(0, 50);
  if (a?.getAttribute("href")) {
    const href = a.getAttribute("href")!;
    if (href.startsWith("https://wa.me") || href.includes("whatsapp")) return "whatsapp";
    if (href.startsWith("tel:")) return "telefone";
    if (href.startsWith("mailto:")) return "email";
    return `link: ${text || href.slice(0, 60)}`;
  }
  return `botão: ${text}`;
}

function onClick(e: MouseEvent) {
  const t = e.target as Element | null;
  const el = t?.closest("a,button,[data-track],[role='button']");
  if (!el) return;
  const doc = document.documentElement;
  track("click", { target: labelOf(el), props: { x: Math.round((e.pageX / Math.max(doc.scrollWidth, 1)) * 100), y: Math.round(e.pageY) } });
}

let started = false;
export function startTracker() {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener("scroll", onScroll, { passive: true });
  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      emitTimes(false);
      if (pageStart) { accumulated += Date.now() - pageStart; pageStart = 0; }
      sectionSince.clear();
      flush();
    } else {
      pageStart = Date.now();
      const now = Date.now();
      sectionActive.forEach((id) => sectionSince.set(id, now));
    }
  });
  window.addEventListener("pagehide", () => { emitTimes(false); flush(); });
}
